"use client";

import {
  OutcomeToolbarFilterSelect,
  useOutcomeToolbarPills,
  type OutcomeToolbarFilterAction,
} from "@/components/ui/primitives/outcome-toolbar-filter-select";
import {
  applyProcedureFilterValues,
  isProcedureUnassignedMode,
  procedureFilterTriggerPills,
  splitProcedureFilterValues,
  type ProcedureFilterAction,
} from "@/lib/constants/analysis-list-filters";
import type { SearchableSelectOption } from "@/components/ui/primitives/searchable-select";
import type { ReactNode } from "react";

const UNASSIGNED_NOTICE =
  "Procedure names were cleared. List shows analyses with no procedure. Pick a name or Select all to leave this mode.";

export interface ProcedureFilterSelectProps {
  readonly value: string[];
  readonly onChange: (value: string[]) => void;
  readonly options: SearchableSelectOption[];
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly disabled?: boolean;
  readonly dropdownHeader?: ReactNode;
  readonly "aria-label"?: string;
}

function applyProcedureToolbarAction(
  current: readonly string[],
  action: OutcomeToolbarFilterAction,
): string[] {
  const procedureAction: ProcedureFilterAction =
    action.type === "set-ids"
      ? { type: "set-procedure-ids", procedureIds: action.ids }
      : action;
  return applyProcedureFilterValues(current, procedureAction);
}

export function ProcedureFilterSelect({
  value,
  onChange,
  options,
  placeholder = "Procedure",
  searchPlaceholder = "Search procedure…",
  disabled = false,
  dropdownHeader,
  "aria-label": ariaLabel,
}: ProcedureFilterSelectProps) {
  const split = splitProcedureFilterValues(value);
  const pills = useOutcomeToolbarPills(
    value,
    options,
    procedureFilterTriggerPills,
  );

  return (
    <OutcomeToolbarFilterSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      disabled={disabled}
      dropdownHeader={dropdownHeader}
      aria-label={ariaLabel}
      selectedIds={split.procedureIds}
      unassignedMode={isProcedureUnassignedMode(value)}
      pass={split.procedurePassed.includes("true")}
      fail={split.procedurePassed.includes("false")}
      unassignedNotice={UNASSIGNED_NOTICE}
      pills={pills}
      applyAction={(action) => applyProcedureToolbarAction(value, action)}
    />
  );
}
